import styled, { keyframes } from "styled-components";
import { COLORS } from "../../common/colors";

// ─── Palette ───────────────────────────────────────────────────────────────────
// This screen uses a warm terracotta/cream theme distinct from the rest of the
// site's cool-green tokens, so the accent/neutral colors are defined locally.
// The semantic error color is still pulled from the shared theme for consistency.

const CREAM       = "#FBF1E3";
const CREAM_SOFT  = "#FDF6EC";
const CARD_BG     = "#FFFFFF";
const TEXT_DARK   = "#3C2A1C";
const TEXT_MUTED  = "#8A7565";
const ACCENT      = "#C1662E";
const ACCENT_DARK = "#9A4A1E";
const ACCENT_LIGHT = "#F5DFC0";
const BORDER      = "#EFE1CB";

const error   = COLORS.error;
const errorBg = COLORS.bgWarmLight;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const kFadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

const kSlideDown = keyframes`
  from { opacity: 0; max-height: 0;   transform: translateY(-8px); }
  to   { opacity: 1; max-height: 200px; transform: translateY(0);  }
`;

const kSpinRing = keyframes`
  to { transform: rotate(360deg); }
`;

const kPopIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.7); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1);   }
`;

// breakpoint where the layout collapses to a single column
const BP = "860px";

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE SHELL
// ═══════════════════════════════════════════════════════════════════════════════

export const PageWrap = styled.section`
  position: relative;
  overflow: hidden;
  background: ${CREAM};
  min-height: 100vh;
  padding: 64px 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: ${BP}) {
    min-height: auto;
    padding: 48px 18px 40px;
  }
`;
PageWrap.displayName = "PageWrap";

export const Grid = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1040px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: center;

  @media (max-width: ${BP}) {
    grid-template-columns: 1fr;
    gap: 32px;
    text-align: center;
  }
`;
Grid.displayName = "Grid";

// ─── Decorative background shapes (abstract, no external image needed) ───────

export const DecorBlob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  pointer-events: none;
  z-index: 0;
  background: ${ACCENT_LIGHT};
  opacity: 0.6;

  &.d1 { width: 320px; height: 320px; top: -100px; right: -80px; }
  &.d2 { width: 260px; height: 260px; bottom: -80px; left: -60px; }
`;
DecorBlob.displayName = "DecorBlob";

export const DecorLeaf = styled.svg`
  position: absolute;
  left: -20px;
  bottom: -20px;
  width: 200px;
  height: auto;
  opacity: 0.5;
  pointer-events: none;
  z-index: 0;

  @media (max-width: ${BP}) {
    width: 140px;
    left: 50%;
    transform: translateX(-72%);
  }
`;
DecorLeaf.displayName = "DecorLeaf";

// ═══════════════════════════════════════════════════════════════════════════════
// HERO / LEFT COLUMN
// ═══════════════════════════════════════════════════════════════════════════════

export const HeroCol = styled.div`
  position: relative;
  z-index: 1;
  animation: ${kFadeUp} 0.55s ease both;
`;
HeroCol.displayName = "HeroCol";

export const Eyebrow = styled.p`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${ACCENT};
  margin-bottom: 14px;

  &::before, &::after { content: "✦"; font-size: 0.7rem; }

  @media (max-width: ${BP}) { justify-content: center; }
`;
Eyebrow.displayName = "Eyebrow";

export const Title = styled.h2`
  font-family: "Playfair Display", Georgia, "Times New Roman", serif;
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  font-weight: 700;
  color: ${TEXT_DARK};
  line-height: 1.2;
  margin-bottom: 14px;
  letter-spacing: -0.01em;
`;
Title.displayName = "Title";

export const OrnamentDivider = styled.svg`
  display: block;
  width: 90px;
  height: 18px;
  margin-bottom: 18px;
  color: ${ACCENT};

  @media (max-width: ${BP}) { margin-left: auto; margin-right: auto; }
`;
OrnamentDivider.displayName = "OrnamentDivider";

export const Subtitle = styled.p`
  font-size: 0.98rem;
  color: ${TEXT_MUTED};
  line-height: 1.7;
  max-width: 380px;
  margin-bottom: 32px;

  @media (max-width: ${BP}) { margin: 0 auto 28px; }
`;
Subtitle.displayName = "Subtitle";

// ─── Trust list — desktop: icon + title + description, vertical stack ────────

export const TrustList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;

  @media (max-width: ${BP}) { display: none; }
`;
TrustList.displayName = "TrustList";

export const TrustItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  text-align: left;
`;
TrustItem.displayName = "TrustItem";

export const TrustIconCircle = styled.div`
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${ACCENT_LIGHT};
  color: ${ACCENT_DARK};

  svg { width: 20px; height: 20px; }
`;
TrustIconCircle.displayName = "TrustIconCircle";

export const TrustTitle = styled.h4`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${TEXT_DARK};
  margin-bottom: 3px;
`;
TrustTitle.displayName = "TrustTitle";

export const TrustDesc = styled.p`
  font-size: 0.85rem;
  color: ${TEXT_MUTED};
  line-height: 1.5;
  margin: 0;
`;
TrustDesc.displayName = "TrustDesc";

// ─── Trust row — mobile only: icon + short label, three across ──────────────

export const MobileTrustRow = styled.div`
  display: none;

  @media (max-width: ${BP}) {
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    gap: 10px;
    max-width: 1040px;
    margin: 32px auto 0;
    padding: 0 4px;
  }
`;
MobileTrustRow.displayName = "MobileTrustRow";

export const MobileTrustItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;
MobileTrustItem.displayName = "MobileTrustItem";

export const MobileTrustIconCircle = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${ACCENT_LIGHT};
  color: ${ACCENT_DARK};

  svg { width: 18px; height: 18px; }
`;
MobileTrustIconCircle.displayName = "MobileTrustIconCircle";

export const MobileTrustLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  color: ${TEXT_DARK};
  text-align: center;
  line-height: 1.3;
`;
MobileTrustLabel.displayName = "MobileTrustLabel";

// ═══════════════════════════════════════════════════════════════════════════════
// FORM CARD / RIGHT COLUMN
// ═══════════════════════════════════════════════════════════════════════════════

export const FormCol = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  animation: ${kFadeUp} 0.6s ease both;
  animation-delay: 0.08s;
`;
FormCol.displayName = "FormCol";

export const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 380px;
  background: ${CARD_BG};
  border: 1px solid ${BORDER};
  border-radius: 20px;
  padding: 48px 28px 28px;
  box-shadow: 0 20px 45px -20px rgba(154, 74, 30, 0.28), 0 2px 8px rgba(0,0,0,0.04);

  @media (max-width: ${BP}) {
    max-width: 100%;
    padding: 44px 20px 24px;
    border-radius: 18px;
  }
`;
Card.displayName = "Card";

export const BadgeCircle = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK});
  box-shadow: 0 10px 24px -8px rgba(154, 74, 30, 0.55);
  animation: ${kPopIn} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  animation-delay: 0.15s;

  svg { width: 26px; height: 26px; color: #fff; }
`;
BadgeCircle.displayName = "BadgeCircle";

export const FormInner = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
FormInner.displayName = "FormInner";

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
`;
FieldGroup.displayName = "FieldGroup";

export const FieldLabel = styled.label`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${TEXT_MUTED};
`;
FieldLabel.displayName = "FieldLabel";

export const InputShell = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;
InputShell.displayName = "InputShell";

export const InputIcon = styled.span`
  position: absolute;
  left: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ACCENT};
  pointer-events: none;

  svg { width: 17px; height: 17px; }
`;
InputIcon.displayName = "InputIcon";

export const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px 12px 40px;
  border-radius: 10px;
  border: 1.5px solid ${({ $error }) => ($error ? error : BORDER)};
  background: ${CREAM_SOFT};
  font-size: 0.92rem;
  color: ${TEXT_DARK};
  min-height: 46px;
  -webkit-appearance: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder { color: ${TEXT_MUTED}; opacity: 0.7; }

  &:focus {
    outline: none;
    border-color: ${ACCENT};
    box-shadow: 0 0 0 3px ${ACCENT_LIGHT};
  }

  &:disabled { opacity: 0.55; cursor: not-allowed; }

  /* keeps iOS from zooming in on focus */
  @media (max-width: ${BP}) { font-size: 16px; }
`;
Input.displayName = "Input";

export const FieldError = styled.span`
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  color: ${error};
  animation: ${kSlideDown} 0.2s ease both;
`;
FieldError.displayName = "FieldError";

// ─── Submit button ────────────────────────────────────────────────────────────

export const SubmitBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 13px 18px;
  border-radius: 12px;
  border: none;
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #ffffff;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  margin-top: 6px;
  min-height: 50px;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s, opacity 0.2s;

  background: ${({ disabled }) =>
    disabled ? "#e2c7ab" : `linear-gradient(135deg, ${ACCENT_DARK}, ${ACCENT})`};
  box-shadow: ${({ disabled }) =>
    disabled ? "none" : "0 10px 24px -8px rgba(154, 74, 30, 0.45)"};

  svg { width: 15px; height: 15px; transition: transform 0.2s; flex-shrink: 0; }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 14px 30px -8px rgba(154, 74, 30, 0.55);
    svg { transform: translateX(3px); }
  }
  &:active:not(:disabled) { transform: translateY(0) scale(0.98); }
  &:focus-visible { outline: 3px solid ${ACCENT}; outline-offset: 2px; }
`;
SubmitBtn.displayName = "SubmitBtn";

export const SpinnerRing = styled.span`
  display: inline-block;
  width: 16px; height: 16px;
  border: 2.5px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${kSpinRing} 0.7s linear infinite;
  flex-shrink: 0;
`;
SpinnerRing.displayName = "SpinnerRing";

// ─── Privacy note + status note ───────────────────────────────────────────────

export const PrivacyNote = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 2px 0 0;
  font-size: 0.74rem;
  color: ${TEXT_MUTED};
  text-align: center;
  line-height: 1.4;

  svg { width: 13px; height: 13px; flex-shrink: 0; color: ${ACCENT}; }
`;
PrivacyNote.displayName = "PrivacyNote";

export const Note = styled.p`
  margin: 0;
  font-size: 0.82rem;
  font-weight: 600;
  text-align: center;
  padding: 9px 12px;
  border-radius: 10px;
  animation: ${kFadeUp} 0.3s ease both;

  color:      ${({ $tone }) => ($tone === "error" ? error : ACCENT_DARK)};
  background: ${({ $tone }) => ($tone === "error" ? errorBg : ACCENT_LIGHT)};
`;
Note.displayName = "Note";